import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Observable, lastValueFrom } from 'rxjs';
import { UserModel } from 'src/app/models/User.model';
import { RedirectProperty, RedirectTarget } from 'src/app/types';
import { environment } from 'src/environments/environment';
import { EmployeeService } from '../employee-service/employee.service';
import { UserService } from '../user/user.service';

@Injectable({
  providedIn: 'root',
})
export class CheckRolesService {
  private static readonly BENEFITS_STATEMENT_ROLE_ID = '33298334-4f15-4403-96ed-5d3855328799';

  constructor(
    private storage: Storage,
    private employeeService: EmployeeService,
    private userService: UserService,
  ) {}

  userUrl = 'users/api/v1/';
  private subject = new BehaviorSubject<any>(null);
  user$: Observable<UserModel> = this.subject.asObservable();
  employee: UserModel = new UserModel();
  targets: RedirectTarget = environment.redirect;

  async getAuthRole(): Promise<any> {
    return await this.storage.get('roles');
  }

  async checkLineControlRole() {
    let roles = await this.getAuthRole();
    let checkRole = false;

    checkRole = roles.find(
      (x: string) =>
        x === 'c78a87f6-7341-44e0-9acc-4068c811f42f' ||
        x === '1a62e03f-6b74-4dda-8365-c79139f5ca00' ||
        x === 'd689b92a-6cf8-455e-a86c-64ef86068f7a',
    );

    if (checkRole) {
      return true;
    } else {
      return false;
    }
  }

  async checkThematicScheduleRole() {
    let roles = await this.getAuthRole();
    let checkRole = false;

    checkRole = roles.find(
      (x: string) => x === '2d4124a1-835e-4910-8ddc-2073d01b5386' || x === '1520a1f7-d5a1-4c10-b96e-6dda5ec954d4',
      //  Roles de administrador e role de view de agenda temática, respectivamente
    );

    if (checkRole) {
      return true;
    } else {
      return false;
    }
  }

  async checkAdminLineControlRole() {
    let contains = false;
    await this.storage.get('roles').then(roles => {
      if (roles.includes('c78a87f6-7341-44e0-9acc-4068c811f42f')) {
        contains = true;
      }
    });

    if (contains) {
      return true;
    } else {
      return false;
    }
  }

  async getEmployee(id: string) {
    this.employee = await lastValueFrom(this.employeeService.getCollaborator(id));
  }

  async getUser() {
    return await lastValueFrom(this.userService.getMe());
  }

  async getEmployeeId(): Promise<string> {
    return await this.storage.get('employeeId').then((id: string) => {
      return id;
    });
  }

  async checkPayments(): Promise<boolean> {
    let employeeId = await this.getEmployeeId();
    await this.getEmployee(employeeId);

    return this.employee.createdAtBackOffice;
  }

  // Verifica se o usuário tem o papel de "Atualização cadastral"
  async checkEmployeeUpdate(): Promise<boolean> {
    let user = await this.getUser();

    return user.roles.find((x: any) =>
      [
        'f1e51ca2-21bb-4087-ae7a-67b98a9f2f10', // HML
        '815d8819-a970-4659-ad86-72ded7b99e91', // PRD
      ].includes(x.id),
    );
  }

  async checkBenefitsStatementAccess(user?: any): Promise<boolean> {
    if (!user) {
      user = await this.getUser();
    }

    return user.roles.some((x: any) => x.id === CheckRolesService.BENEFITS_STATEMENT_ROLE_ID);
  }

  // Verifica se o usuário tem o papel passado por parâmetro
  async checkAccessRole(property: RedirectProperty, user?: any): Promise<boolean> {
    // Se o usuário já foi passado por parâmetro, utiliza ele, caso contrário, busca o usuário atual
    if (!user) {
      user = await this.getUser();
    }

    return user.roles.find((x: any) => x.id === property.role || property.roles?.includes(x.id));
  }

  // Verifica se o usuário possui o papel/perm para login SSO
  async checkSsoRole(): Promise<boolean> {
    try {
      let user = await this.getUser();
      return !!user.roles.find((x: any) => x.id === 'e868801f-2826-4ad3-ad16-50fef7616bbb');
    } catch {
      return false;
    }
  }

  async checkEmployeeEmail() {
    let employeeId = await this.getEmployeeId();

    await this.getEmployee(employeeId);
    return this.employee.email.length > 0 ? true : false;
  }
}
